// src/components/ResultsPage.jsx

import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
// auth eklendi (Kullanıcı bilgisini almak için)
import { db, auth } from "../firebase"; 
import { doc, getDoc } from "firebase/firestore";

// AI Analiz Servisi eklendi
import { analyzeTestResults } from "../services/aiAnalysis"; 

import {
  Chart as ChartJS,
  RadialLinearScale,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";

import { Radar, Bar } from "react-chartjs-2";

ChartJS.register(
  RadialLinearScale,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend
);

// Statik stil tanımları (Daha önce entegre ettiğimiz CSS class'larını kullanacağız)
const styles = {
    reportContainer: { whiteSpace: "pre-line", background: '#f8f9fa', padding: 25, borderRadius: 12, borderLeft: '4px solid #007bff', lineHeight: 1.6, marginTop: 30 },
    subtitle: { fontSize: '1.5em', color: '#343a40', borderBottom: '1px solid #dee2e6', paddingBottom: 10, marginBottom: 20, marginTop: 40 },
    loading: { padding: 40, textAlign: 'center', fontSize: '1.2em' }
};


export default function ResultsPage() {
  const { testKey } = useParams();
  const navigate = useNavigate();
  const user = auth.currentUser; // Kullanıcı bilgisini al
  
  const [testResults, setTestResults] = useState(null); // Firestore'dan gelen tüm sonuç (scores, answers, isCompleted)
  const [aiReport, setAiReport] = useState(""); // <-- useState eklendi
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


  // --- AI RAPOR OLUŞTURMA FONKSİYONU ---
  // Bu fonksiyon, mevcut yapınızdaki analyzeTestResults çağrısının işini yapar.
  // Ancak, ResultsPage'de zaten fetchAndAnalyze içinde çağrıldığı için burada tutmaya gerek yoktur.
  // Eğer bu fonksiyonu AYRI bir butona bağlamak isterseniz kullanabilirsiniz.
  /*
  const generateReport = async () => {
    if (!testResults || !testResults.scores) {
        setError("Test sonuçları yüklenemedi.");
        return;
    }
    setLoading(true);
    setError(null);
    try {
        // analyzeTestResults servisini kullanıyoruz
        const reportText = await analyzeTestResults(testKey, testResults.scores);
        setAiReport(reportText);
    } catch (err) {
        console.error("Manuel rapor oluşturma hatası:", err);
        setError("Yapay zeka raporu oluşturulurken bir hata oluştu.");
    } finally {
        setLoading(false);
    }
  };
  */

  // --- 1. VERİ ÇEKME VE AI RAPORU OLUŞTURMA (useEffect ile otomatik yükleme) ---
  useEffect(() => {
    if (!user) {
        navigate("/login");
        return;
    }
    
    setLoading(true);

    async function fetchAndAnalyze() {
        try {
            const resultsRef = doc(db, "users", user.uid, "answers", testKey);
            const snapshot = await getDoc(resultsRef);

            if (!snapshot.exists() || !snapshot.data().isCompleted) {
                throw new Error("Test tamamlanmamış veya sonuç bulunamadı.");
            }
            
            const resultsData = snapshot.data();
            setTestResults(resultsData);
            
            // AI Raporunu otomatik olarak oluştur/çek
            const reportText = await analyzeTestResults(testKey, resultsData.scores);
            setAiReport(reportText);

        } catch (err) {
            console.error("Sonuç yükleme hatası:", err);
            setError(err.message || "Sonuçlar ve AI raporu yüklenirken bir hata oluştu.");
        } finally {
            setLoading(false);
        }
    }

    fetchAndAnalyze();
  }, [testKey, user, navigate]);


  // --- 2. GRAFİK VERİLERİNİ HAZIRLAMA (useMemo) ---
  const chartData = useMemo(() => {
    // ... (Grafik veri hazırlama kısmı değişmedi)
    if (!testResults || !testResults.scores) return null;
    const scores = testResults.scores;

    if (testKey === "disc") {
        const labels = ["D: Baskınlık", "I: Etkileme", "S: Durağanlık", "C: Uyum"];
        const values = labels.map(label => scores[label.charAt(0)] || 0);

        return {
            labels,
            datasets: [{
                label: "DISC Puanları",
                data: values,
                backgroundColor: "rgba(74, 108, 247, 0.7)",
                borderColor: "rgba(74, 108, 247, 1)",
                borderWidth: 1,
            }],
        };
    } 
    
    else if (testKey === "riasec") {
        const labels = ["R: Gerçekçi", "I: Araştırmacı", "A: Sanatsal", "S: Sosyal", "E: Girişimci", "C: Geleneksel"];
        const values = labels.map(label => scores[label.charAt(0)] || 0);

        return {
            labels,
            datasets: [{
                label: "RIASEC Profili",
                data: values,
                backgroundColor: "rgba(0, 188, 212, 0.6)",
                borderColor: "rgba(0, 188, 212, 1)",
                borderWidth: 2,
                pointBackgroundColor: "rgba(0, 188, 212, 1)",
                pointBorderColor: "#fff",
            }],
        };
    } 
    
    else if (testKey === "ocean") {
        const labels = ["O: Açıklık", "C: Sorumluluk", "E: Dışadönüklük", "A: Uyumluluk", "N: Nevrotiklik"];
        const values = labels.map(label => scores[label.charAt(0)] || 0);

        return {
            labels,
            datasets: [{
                label: "OCEAN Puanları",
                data: values,
                backgroundColor: "rgba(255, 159, 64, 0.7)",
                borderColor: "rgba(255, 99, 32, 1)",
                borderWidth: 1,
            }],
        };
    }
    
    return null;
  }, [testResults, testKey]);

  // --- 3. GRAFİK OPSİYONLARI ---
  const chartOptions = useMemo(() => {
    // ... (Grafik opsiyonları kısmı değişmedi)
    if (testKey === "riasec") {
        return {
            scales: {
                r: {
                    angleLines: { display: false },
                    suggestedMin: 0,
                    suggestedMax: 20, // RIASEC max puanına göre ayarlanabilir
                    pointLabels: {
                        font: { size: 14 }
                    }
                }
            },
            plugins: {
                legend: { display: false }
            }
        };
    }
    return {
        scales: {
            y: {
                beginAtZero: true
            }
        },
        plugins: {
            legend: { display: false }
        }
    };
  }, [testKey]);


  // --- 4. RENDER ---
  if (loading) return <div className="test-page" style={styles.loading}>Sonuçlarınız ve Yapay Zeka Raporunuz hazırlanıyor...</div>;
  if (error) return <div className="test-page error-message">Hata: {error}</div>;
  if (!chartData) return <div className="test-page error-message">Hata: Grafik verisi oluşturulamadı.</div>;

  
  let chartComponent;
  let chartType;

  if (testKey === "riasec") {
    chartComponent = <Radar data={chartData} options={chartOptions} />;
    chartType = "Holland'ın Altıgen Modeli (RIASEC)";
  } else {
    chartComponent = <Bar data={chartData} options={chartOptions} />;
    chartType = (testKey === "disc" ? "DISC Profil" : "OCEAN Profili");
  }


  return (
    // 'test-page' class'ı, CSS'teki stilleri kullanmak için
    <div className="test-page">
        <h1>{testKey.toUpperCase()} Test Sonuçlarınız</h1>
        
        {/* Grafik Bölümü */}
        <div style={styles.subtitle}>📊 {chartType} Grafiği</div>
        <div style={{ height: '400px', marginBottom: '40px' }}>
            {chartComponent}
        </div>
        
        {/* AI Raporu Bölümü - Otomatik Yükleme Yapıldığı İçin Burası Sadece Görüntüleme */}
        <div style={styles.subtitle}>🧠 Yapay Zeka Analiz Raporunuz</div>
        {aiReport ? (
            // NOT: reportContainer stili zaten whiteSpace: "pre-line" içeriyor.
            <div style={styles.reportContainer}>
                {aiReport}
            </div>
        ) : (
             <div className="error-message">Yapay zeka raporu oluşturulamadı.</div>
        )}
        
        {/* Genel Rapor Sayfasına Yönlendirme (Daha önce eklediğimiz buton) */}
        <button 
            onClick={() => navigate('/ai-report')} 
            className="nav-btn next-btn" 
            style={{marginTop: '30px', float: 'right'}}
        >
            Tüm Sonuçları Sentezleyen Genel Raporu Gör →
        </button>
    </div>
  );
}